import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { salesApi } from '../api/endpoints';
import KPICard from '../components/KPICard';
import DateRangeFilter from '../components/DateRangeFilter';
import DataTable from '../components/DataTable';
import TimeSeriesChart from '../components/charts/TimeSeriesChart';
import CohortHeatmap from '../components/charts/CohortHeatmap';
import { useTheme } from '../theme/ThemeContext';
import { defaultDateRangeFilters } from '../utils/defaultDateFilters';
import LoadingWait from '../components/LoadingWait';
import { IconInfo } from '../components/Icons';
const TABS = ['Общее', 'SKU', 'Клиенты', 'Дистрибьюторы', 'Клиники', 'Когорты'];

function filterRowsBySearch(rows, query, fields) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((row) =>
    fields.some((f) => {
      const v = row[f];
      if (v == null) return false;
      return String(v).toLowerCase().includes(q);
    }),
  );
}

export default function SalesDashboard() {
  const { theme } = useTheme();
  const tabs = TABS;

  const [filters, setFilters] = useState(() => defaultDateRangeFilters());
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [byDate, setByDate] = useState([]);
  const [clients, setClients] = useState([]);
  const [primarySecondary, setPrimarySecondary] = useState(null);
  const [skus, setSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(null);
  const [skuDeals, setSkuDeals] = useState([]);
  const [skuDealsLoading, setSkuDealsLoading] = useState(false);
  const [cohort, setCohort] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [selectedDistributorCompany, setSelectedDistributorCompany] = useState(null);
  const [distributorCompanyDeals, setDistributorCompanyDeals] = useState([]);
  const [distributorCompanyDealsLoading, setDistributorCompanyDealsLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [psListType, setPsListType] = useState(null);
  const [psList, setPsList] = useState([]);
  const [psListLoading, setPsListLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [clientDeals, setClientDeals] = useState([]);
  const [clientDealsLoading, setClientDealsLoading] = useState(false);
  const [clientListSearch, setClientListSearch] = useState('');
  const [distributorListSearch, setDistributorListSearch] = useState('');
  const [clinicListSearch, setClinicListSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const resetTabData = useCallback(() => {
    setClients([]); setSkus([]); setCohort(null); setDistributors([]); setClinics([]);
    setPrimarySecondary(null);
    setSelectedSku(null); setSkuDeals([]); setSelectedDistributor(null); setSelectedClinic(null);
    setSelectedDistributorCompany(null); setDistributorCompanyDeals([]);
    setPsListType(null); setPsList([]);
    setSelectedClient(null); setSelectedGroup(null); setClientDeals([]);
    setClientListSearch('');
    setDistributorListSearch('');
    setClinicListSearch('');
  }, []);

  const togglePsList = async (type) => {
    if (psListType === type) { setPsListType(null); setPsList([]); return; }
    setPsListType(type);
    setPsListLoading(true);
    try {
      const r = await salesApi.primarySecondaryList({ ...filters, client_type: type });
      setPsList(r.data || []);
    } catch { setPsList([]); }
    setPsListLoading(false);
  };

  const _loadDealsForClient = async (cid) => {
    setSelectedClient(cid);
    setClientDealsLoading(true);
    try {
      const r = await salesApi.clientDeals({ ...filters, client_id: cid });
      setClientDeals(r.data || []);
    } catch { setClientDeals([]); }
    setClientDealsLoading(false);
  };

  // Клик по строке в главной таблице клиентов
  const onClientRowClick = (row) => {
    if (row.is_group) {
      const gid = row.client_id;
      if (selectedGroup?.client_id === gid) {
        setSelectedGroup(null);
      } else {
        setSelectedGroup(row);
        setSelectedClient(null);
        setClientDeals([]);
      }
    } else {
      setSelectedGroup(null);
      const cid = row.client_id;
      if (selectedClient === cid) { setSelectedClient(null); setClientDeals([]); return; }
      _loadDealsForClient(cid);
    }
  };

  // Клик по юрлицу внутри раскрытой группы
  const onGroupMemberClick = (row) => {
    const cid = row.client_id;
    if (selectedClient === cid) { setSelectedClient(null); setClientDeals([]); return; }
    _loadDealsForClient(cid);
  };

  const onDistributorGroupClick = (row) => {
    const key = row?.group_name || row?.client_id;
    const selectedKey = selectedDistributor?.group_name || selectedDistributor?.client_id;
    if (selectedKey === key) {
      setSelectedDistributor(null);
      setSelectedDistributorCompany(null);
      setDistributorCompanyDeals([]);
      return;
    }
    setSelectedDistributor(row);
    setSelectedDistributorCompany(null);
    setDistributorCompanyDeals([]);
  };

  const onDistributorCompanyClick = async (row) => {
    const cid = row?.client_id;
    if (!cid) return;
    if (selectedDistributorCompany?.client_id === cid) {
      setSelectedDistributorCompany(null);
      setDistributorCompanyDeals([]);
      return;
    }
    setSelectedDistributorCompany(row);
    setDistributorCompanyDealsLoading(true);
    try {
      const r = await salesApi.clientDeals({ ...filters, client_id: cid });
      setDistributorCompanyDeals(r.data || []);
    } catch {
      setDistributorCompanyDeals([]);
    }
    setDistributorCompanyDealsLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    resetTabData();
    try {
      const [ov, bd] = await Promise.all([
        salesApi.overview(filters),
        salesApi.byDate(filters),
      ]);
      setOverview(ov.data);
      setByDate(bd.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters, resetTabData]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!infoOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setInfoOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [infoOpen]);

  useEffect(() => {
    const f = filters;
    if (tab === 1 && skus.length === 0)
      salesApi.skus(f).then(r => setSkus(r.data)).catch(() => {});
    if (tab === 2 && clients.length === 0) {
      salesApi.clientsGrouped(f).then(r => setClients(r.data)).catch(() => {});
      salesApi.primarySecondary(f).then(r => setPrimarySecondary(r.data)).catch(() => {});
    }
    if (tab === 3 && distributors.length === 0)
      salesApi.topClientsDynamics({ ...f, segment: 'distributors' }).then(r => setDistributors(r.data)).catch(() => {});
    if (tab === 4 && clinics.length === 0)
      salesApi.topClientsDynamics({ ...f, segment: 'clinics', limit: 50 }).then(r => setClinics(r.data)).catch(() => {});
    if (tab === 5 && !cohort)
      salesApi.cohort(f).then(r => setCohort(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  useEffect(() => {
    if (!selectedSku?.sku_name) {
      setSkuDeals([]);
      return;
    }
    let cancelled = false;
    setSkuDealsLoading(true);
    salesApi
      .skuDeals({ ...filters, sku_name: selectedSku.sku_name })
      .then((r) => {
        if (!cancelled) setSkuDeals(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setSkuDeals([]);
      })
      .finally(() => {
        if (!cancelled) setSkuDealsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedSku?.sku_name, filters]);

  const o = overview || {};
  const ps = primarySecondary || {};

  const filteredClients = useMemo(() => {
    const q = (clientListSearch || '').trim().toLowerCase();
    if (!q) return clients || [];
    return (clients || []).filter((row) => {
      if (String(row.client_name || '').toLowerCase().includes(q)) return true;
      if (String(row.client_id || '').toLowerCase().includes(q)) return true;
      if (row.is_group && Array.isArray(row.members)) {
        return row.members.some(
          (m) =>
            String(m.client_name || '').toLowerCase().includes(q) ||
            String(m.client_id || '').toLowerCase().includes(q),
        );
      }
      return false;
    });
  }, [clients, clientListSearch]);
  const filteredDistributors = useMemo(
    () => filterRowsBySearch(distributors, distributorListSearch, ['client_id', 'group_name']),
    [distributors, distributorListSearch],
  );
  const filteredClinics = useMemo(
    () => filterRowsBySearch(clinics, clinicListSearch, ['client_id']),
    [clinics, clinicListSearch],
  );
  const skuChartData = selectedSku
    ? (selectedSku.monthly_month_labels || []).map((m, i) => ({
        date: m,
        revenue: (selectedSku.monthly_revenue_series || [])[i] || 0,
        customers: (selectedSku.monthly_customers_series || [])[i] || 0,
        new_customers: (selectedSku.monthly_new_customers_series || [])[i] || 0,
      }))
    : [];

  const buildClientChartData = (item) =>
    item ? (item.series || []).map(p => ({ date: p.date, revenue: p.value || 0 })) : [];

  return (
    <div style={styles.page} className="dashboard-panel-enter">
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.h1}>Продажи</h1>
        </div>
        <div style={styles.headActions}>
          <button
            type="button"
            className="dash-toolbar-btn"
            style={styles.infoBtn}
            onClick={() => {
              setInfoOpen(true);
              const main = document.querySelector('main');
              if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
              else window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <IconInfo style={{ color: 'var(--accent)', flexShrink: 0 }} />
            Важная информация
          </button>
        </div>
      </div>

      {infoOpen && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setInfoOpen(false)}
          role="presentation"
        >
          <div
            style={styles.modalPanel}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="sales-disclaimer-title"
          >
            <div style={styles.modalHeader}>
              <h2 id="sales-disclaimer-title" style={styles.modalTitle}>Важная информация</h2>
              <button type="button" style={styles.modalClose} onClick={() => setInfoOpen(false)} aria-label="Закрыть">
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalP}>
                Расхождения с выручкой из 1С и дашборда никак не убрать! Потому что в дашборде информация
                отображается по дате <strong>заказа</strong>. А 1С выгружает табличку за выбранный период
                (с учётом фильтра хоз. операции = реализация) по дате <strong>реализации</strong> этого заказа.
              </p>
              <p style={styles.modalStrong}>Но!</p>
              <p style={styles.modalP}>
                Данные верны и там и там. Просто есть небольшие расхождения: заказы могут «перевалить» в следующий
                месяц, например, или наоборот. Номера заказов, имена клиентов, суммы заказов и т.д. соответствуют 1С.
              </p>
              <p style={styles.modalFooterNote}>Приятного просмотра :)</p>
            </div>
          </div>
        </div>
      )}

      <DateRangeFilter filters={filters} onChange={setFilters} />

      <div style={styles.kpiRow}>
        <KPICard title="Выручка" value={o.total_revenue} suffix=" ₽" delta={o.delta_revenue} dimmed={loading} />
        <KPICard title="Заказы" value={o.total_orders} delta={o.delta_orders} dimmed={loading} />
        <KPICard title="Клиенты" value={o.total_customers} delta={o.delta_customers} dimmed={loading} />
        <KPICard title="ARPU" value={o.arpu} suffix=" ₽" delta={o.delta_arpu} dimmed={loading} />
        <KPICard title="LTV" value={o.ltv} suffix=" ₽" dimmed={loading} />
        <KPICard title="Ср. чек" value={o.avg_check} suffix=" ₽" delta={o.delta_avg_check} dimmed={loading} />
      </div>

      <div className="dash-main-tabs" role="tablist" aria-label="Разделы продаж" aria-busy={loading}>
        {tabs.map((t, i) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === i}
            onClick={() => setTab(i)}
            className={`dash-main-tab${tab === i ? ' dash-main-tab--active' : ''}`}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.section} className="dash-section">
          <LoadingWait />
        </div>
      ) : (
        <>
      {tab === 0 && (
        <div style={styles.section} className="dash-section">
          <h2 style={styles.h2}>Выручка по месяцам</h2>
          <TimeSeriesChart data={byDate} lines={[{ key: 'revenue', name: 'Выручка', color: 'var(--accent)' }]} />
        </div>
      )}

      {tab === 1 && (
        <div style={styles.section} className="dash-section">
          <div style={styles.sectionHead}>
            <h2 style={{ ...styles.h2, marginBottom: 0 }}>SKU Аналитика</h2>
            {selectedSku && (
              <button
                type="button"
                style={styles.closeXBtn}
                onClick={() => { setSelectedSku(null); setSkuDeals([]); }}
                aria-label="Сбросить выбор SKU"
                title="Сбросить"
              >
                ×
              </button>
            )}
          </div>
          <p style={styles.hint}>Клик по строке — динамика и список покупок по выбранному SKU</p>
          {selectedSku && (
            <div style={styles.chartBlock}>
              <div style={styles.groupPanelHeader}>
                <h3 style={{ ...styles.h2, marginBottom: 0, flex: 1, minWidth: 0 }}>
                  Динамика выручки — {selectedSku.sku_name}
                </h3>
                <button
                  type="button"
                  style={styles.closeXBtn}
                  onClick={() => { setSelectedSku(null); setSkuDeals([]); }}
                  aria-label="Сбросить выбор SKU"
                  title="Сбросить"
                >
                  ×
                </button>
              </div>
              <TimeSeriesChart
                data={skuChartData}
                lines={[
                  { key: 'revenue', name: 'Выручка', color: 'var(--accent)' },
                  { key: 'customers', name: 'Клиенты', color: theme.chartColors[3] },
                  { key: 'new_customers', name: 'Новые клиенты', color: theme.chartColors[1] },
                ]}
              />
              <div style={{ ...styles.groupPanelHeader, marginTop: 22 }}>
                <h3 style={{ ...styles.h2, marginBottom: 0, flex: 1, minWidth: 0 }}>
                  Кто купил — {selectedSku.sku_name}
                  {!skuDealsLoading && skuDeals.length > 0 ? (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: 13 }}>
                      {' '}({skuDeals.length.toLocaleString('ru-RU')})
                    </span>
                  ) : null}
                </h3>
                <button
                  type="button"
                  style={styles.closeXBtn}
                  onClick={() => { setSelectedSku(null); setSkuDeals([]); }}
                  aria-label="Сбросить выбор SKU"
                  title="Сбросить"
                >
                  ×
                </button>
              </div>
              {skuDealsLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Загрузка...</div>
              ) : skuDeals.length > 0 ? (
                <DataTable
                  columns={[
                    { key: 'date', label: 'Дата' },
                    { key: 'client', label: 'Клиент' },
                    { key: 'orders', label: 'Кол-во' },
                    { key: 'revenue', label: 'Выручка', fmt: 'money' },
                    { key: 'territory', label: 'Менеджер' },
                  ]}
                  rows={skuDeals}
                />
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>
                  Нет покупок за выбранный период
                </div>
              )}
            </div>
          )}
          <DataTable
            columns={[
              { key: 'sku_name', label: 'SKU' },
              { key: 'total_revenue', label: 'Выручка', fmt: 'money' },
              { key: 'total_orders', label: 'Кол-во' },
              { key: 'total_customers', label: 'Клиенты' },
              { key: 'new_customers', label: 'Новые клиенты' },
            ]}
            rows={skus}
            onRowClick={(row) => setSelectedSku(row)}
            activeRowKey={selectedSku?.sku_name}
            rowKeyField="sku_name"
          />
        </div>
      )}

      {tab === 2 && (
        <div style={styles.section} className="dash-section">
          <div style={styles.sectionHead}>
            <h2 style={{ ...styles.h2, marginBottom: 0 }}>Клиенты</h2>
            {(selectedGroup || selectedClient) && (
              <button
                type="button"
                style={styles.closeXBtn}
                onClick={() => { setSelectedGroup(null); setSelectedClient(null); setClientDeals([]); }}
                aria-label="Сбросить выбор клиента"
                title="Сбросить"
              >
                ×
              </button>
            )}
          </div>
          <input
            type="search"
            value={clientListSearch}
            onChange={(e) => setClientListSearch(e.target.value)}
            placeholder="Поиск по названию, ID или юрлицу группы…"
            style={styles.tableSearch}
            aria-label="Поиск по клиентам"
          />
          {primarySecondary && (
            <>
              <div style={{ ...styles.kpiRow, marginBottom: 16 }}>
                <button style={{ ...styles.badge, ...(psListType === 'primary' ? styles.badgeActive : {}), cursor: 'pointer' }} onClick={() => togglePsList('primary')}>
                  Первичные: {ps.primary || 0} ({ps.total ? Math.round(ps.primary / ps.total * 100) : 0}%)
                </button>
                <button style={{ ...styles.badge, ...(psListType === 'secondary' ? styles.badgeActive : {}), cursor: 'pointer' }} onClick={() => togglePsList('secondary')}>
                  Вторичные: {ps.secondary || 0} ({ps.total ? Math.round(ps.secondary / ps.total * 100) : 0}%)
                </button>
              </div>
              {psListType && (
                <div style={{ marginBottom: 16 }}>
                  {psListLoading ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Загрузка...</div>
                  ) : (
                    <DataTable
                      title={psListType === 'primary' ? 'Первичные клиенты' : 'Вторичные клиенты'}
                      columns={[
                        { key: 'client_id', label: 'Клиент' },
                        { key: 'first_date', label: 'Первая покупка' },
                      ]}
                      rows={psList}
                    />
                  )}
                </div>
              )}
            </>
          )}

          {/* Раскрытая группа — список юрлиц и их сделки */}
          {selectedGroup && (
            <div style={{ ...styles.chartBlock, marginBottom: 16 }}>
              <div style={styles.groupPanelHeader}>
                <h3 style={{ ...styles.h2, marginBottom: 0 }}>
                  <span style={styles.groupBadge}>Группа</span>
                  {' '}{selectedGroup.group_name}
                </h3>
                <button
                  type="button"
                  style={styles.closeXBtn}
                  onClick={() => { setSelectedGroup(null); setSelectedClient(null); setClientDeals([]); }}
                  aria-label="Сбросить выбор группы"
                  title="Сбросить"
                >
                  ×
                </button>
              </div>
              <p style={{ ...styles.hint, margin: '8px 0 12px' }}>
                Клик по строке — список покупок юрлица
              </p>
              <DataTable
                columns={[
                  { key: 'client_name', label: 'Юрлицо' },
                  { key: 'ltv', label: 'LTV', fmt: 'money' },
                  { key: 'order_lines_count', label: 'Заказы' },
                  { key: 'total_orders', label: 'Кол-во продукции' },
                  { key: 'average_order_value', label: 'Ср. чек', fmt: 'money' },
                  {
                    key: 'is_active',
                    label: 'Статус (6 мес)',
                    render: (v) => (v ? '✓ Активен' : '✗ Неактивен'),
                  },
                ]}
                rows={selectedGroup.members || []}
                onRowClick={onGroupMemberClick}
                activeRowKey={selectedClient}
                rowKeyField="client_id"
              />
              {selectedClient && (
                <div style={{ marginTop: 14 }}>
                  <div style={styles.groupPanelHeader}>
                    <h4 style={{ ...styles.h2, marginBottom: 0, flex: 1, minWidth: 0 }}>
                      Сделки — {(selectedGroup.members || []).find(m => m.client_id === selectedClient)?.client_name || selectedClient}
                    </h4>
                    <button
                      type="button"
                      style={styles.closeXBtn}
                      onClick={() => { setSelectedClient(null); setClientDeals([]); }}
                      aria-label="Сбросить выбор юрлица"
                      title="Сбросить"
                    >
                      ×
                    </button>
                  </div>
                  {clientDealsLoading ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Загрузка...</div>
                  ) : clientDeals.length > 0 ? (
                    <DataTable
                      columns={[
                        { key: 'date', label: 'Дата' },
                        { key: 'revenue', label: 'Выручка', fmt: 'money' },
                        { key: 'orders', label: 'Кол-во' },
                        { key: 'sku', label: 'SKU' },
                        { key: 'territory', label: 'Территория' },
                      ]}
                      rows={clientDeals}
                    />
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Нет сделок за выбранный период</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Сделки одиночного клиента (не из группы) */}
          {!selectedGroup && selectedClient && (
            <div style={{ ...styles.chartBlock, marginBottom: 16 }}>
              <div style={styles.groupPanelHeader}>
                <h3 style={{ ...styles.h2, marginBottom: 0, flex: 1, minWidth: 0 }}>
                  Сделки — {clients.find(c => c.client_id === selectedClient)?.client_name || selectedClient}
                </h3>
                <button
                  type="button"
                  style={styles.closeXBtn}
                  onClick={() => { setSelectedClient(null); setClientDeals([]); }}
                  aria-label="Сбросить выбор клиента"
                  title="Сбросить"
                >
                  ×
                </button>
              </div>
              {clientDealsLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Загрузка...</div>
              ) : clientDeals.length > 0 ? (
                <DataTable
                  columns={[
                    { key: 'date', label: 'Дата' },
                    { key: 'revenue', label: 'Выручка', fmt: 'money' },
                    { key: 'orders', label: 'Кол-во' },
                    { key: 'sku', label: 'SKU' },
                    { key: 'territory', label: 'Территория' },
                  ]}
                  rows={clientDeals}
                />
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Нет сделок за выбранный период</div>
              )}
            </div>
          )}

          <p style={styles.hint}>Клик по группе — юрлица группы; клик по клиенту — список покупок</p>
          <p style={{ ...styles.hint, marginTop: -8, marginBottom: 12 }}>
            Статус: активен, если была покупка в последние 6 месяцев (180 дней) от последней даты в выборке.
          </p>
          <DataTable
            columns={[
              {
                key: 'client_name',
                label: 'Клиент / Группа',
                render: (name, row) => (
                  <span style={styles.groupCellWrap}>
                    {row.is_group ? <span style={styles.groupBadge}>Группа</span> : null}
                    <span>{name}</span>
                  </span>
                ),
              },
              { key: 'ltv', label: 'LTV', fmt: 'money' },
              {
                key: 'expense',
                label: 'Расходы',
                render: (v) => (v ? Math.round(v).toLocaleString('ru-RU') + ' ₽' : '—'),
              },
              { key: 'order_lines_count', label: 'Заказы' },
              { key: 'total_orders', label: 'Кол-во продукции' },
              { key: 'average_order_value', label: 'Ср. чек', fmt: 'money' },
              {
                key: 'is_active',
                label: 'Статус (6 мес)',
                render: (v) => (v ? '✓ Активен' : '✗ Неактивен'),
              },
            ]}
            rows={filteredClients}
            onRowClick={onClientRowClick}
            activeRowKey={selectedGroup?.client_id || selectedClient}
            rowKeyField="client_id"
            pageSize={50}
          />
        </div>
      )}

      {tab === 3 && (
        <div style={styles.section} className="dash-section">
          <div style={styles.sectionHead}>
            <h2 style={{ ...styles.h2, marginBottom: 0 }}>Дистрибьюторы</h2>
            {(selectedDistributor || selectedDistributorCompany) && (
              <button
                type="button"
                className="dash-toolbar-btn"
                style={styles.resetBtn}
                onClick={() => {
                  setSelectedDistributor(null);
                  setSelectedDistributorCompany(null);
                  setDistributorCompanyDeals([]);
                }}
              >
                Сбросить
              </button>
            )}
          </div>
          <input
            type="search"
            value={distributorListSearch}
            onChange={(e) => setDistributorListSearch(e.target.value)}
            placeholder="Поиск по названию или ID…"
            style={styles.tableSearch}
            aria-label="Поиск по дистрибьюторам"
          />
          <p style={styles.hint}>Клик по группе — список компаний и динамика. Клик по компании — её заказы.</p>
          {selectedDistributor && (
            <div style={styles.chartBlock}>
              <div style={styles.groupPanelHeader}>
                <h3 style={{ ...styles.h2, marginBottom: 0 }}>
                  Динамика выручки — {selectedDistributor.group_name || selectedDistributor.client_id}
                </h3>
                <button
                  type="button"
                  className="dash-toolbar-btn"
                  style={styles.resetBtn}
                  onClick={() => {
                    setSelectedDistributor(null);
                    setSelectedDistributorCompany(null);
                    setDistributorCompanyDeals([]);
                  }}
                >
                  Сбросить
                </button>
              </div>
              <TimeSeriesChart data={buildClientChartData(selectedDistributor)} lines={[{ key: 'revenue', name: 'Выручка', color: 'var(--accent)' }]} />
            </div>
          )}
          {selectedDistributor?.is_group && (
            <div style={{ ...styles.chartBlock, marginTop: -4 }}>
              <h3 style={styles.h2}>Компании группы — {selectedDistributor.group_name || selectedDistributor.client_id}</h3>
              <DataTable
                columns={[
                  { key: 'client_id', label: 'Компания' },
                  { key: 'total_revenue', label: 'Выручка', fmt: 'money' },
                  { key: 'total_orders', label: 'Кол-во' },
                ]}
                rows={selectedDistributor.companies || []}
                onRowClick={onDistributorCompanyClick}
                activeRowKey={selectedDistributorCompany?.client_id}
                rowKeyField="client_id"
              />
            </div>
          )}
          {selectedDistributorCompany && (
            <div style={{ ...styles.chartBlock, marginTop: -4 }}>
              <h3 style={styles.h2}>Заказы — {selectedDistributorCompany.client_id}</h3>
              {distributorCompanyDealsLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Загрузка...</div>
              ) : distributorCompanyDeals.length > 0 ? (
                <DataTable
                  columns={[
                    { key: 'date', label: 'Дата' },
                    { key: 'revenue', label: 'Выручка', fmt: 'money' },
                    { key: 'orders', label: 'Кол-во' },
                    { key: 'sku', label: 'SKU' },
                    { key: 'territory', label: 'Территория' },
                  ]}
                  rows={distributorCompanyDeals}
                />
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Нет заказов за выбранный период</div>
              )}
            </div>
          )}
          <DataTable
            columns={[
              {
                key: 'client_id',
                label: 'Группа компаний',
                render: (_v, row) => (
                  <span style={styles.groupCellWrap}>
                    {row?.is_group ? <span style={styles.groupBadge}>Группа</span> : null}
                    <span>{row.group_name || row.client_id}</span>
                  </span>
                ),
              },
              { key: 'total_revenue', label: 'Выручка', fmt: 'money' },
              { key: 'total_orders', label: 'Кол-во' },
            ]}
            rows={filteredDistributors}
            onRowClick={onDistributorGroupClick}
            activeRowKey={selectedDistributor?.group_name || selectedDistributor?.client_id}
            rowKeyField="client_id"
          />
        </div>
      )}

      {tab === 4 && (
        <div style={styles.section} className="dash-section">
          <div style={styles.sectionHead}>
            <h2 style={{ ...styles.h2, marginBottom: 0 }}>Клиники</h2>
            {selectedClinic && (
              <button
                type="button"
                className="dash-toolbar-btn"
                style={styles.resetBtn}
                onClick={() => setSelectedClinic(null)}
              >
                Сбросить
              </button>
            )}
          </div>
          <input
            type="search"
            value={clinicListSearch}
            onChange={(e) => setClinicListSearch(e.target.value)}
            placeholder="Поиск по названию или ID…"
            style={styles.tableSearch}
            aria-label="Поиск по клиникам"
          />
          <p style={styles.hint}>Клик по строке — график динамики выручки</p>
          {selectedClinic && (
            <div style={styles.chartBlock}>
              <div style={styles.groupPanelHeader}>
                <h3 style={{ ...styles.h2, marginBottom: 0 }}>Динамика выручки — {selectedClinic.client_id}</h3>
                <button
                  type="button"
                  className="dash-toolbar-btn"
                  style={styles.resetBtn}
                  onClick={() => setSelectedClinic(null)}
                >
                  Сбросить
                </button>
              </div>
              <TimeSeriesChart data={buildClientChartData(selectedClinic)} lines={[{ key: 'revenue', name: 'Выручка', color: 'var(--accent)' }]} />
            </div>
          )}
          <DataTable
            columns={[
              { key: 'client_id', label: 'Клиент' },
              { key: 'total_revenue', label: 'Выручка', fmt: 'money' },
              { key: 'total_orders', label: 'Кол-во' },
            ]}
            rows={filteredClinics}
            onRowClick={(row) => setSelectedClinic(row)}
            activeRowKey={selectedClinic?.client_id}
            rowKeyField="client_id"
          />
        </div>
      )}

      {tab === 5 && (
        <div style={styles.section} className="dash-section">
          <h2 style={styles.h2}>Когортный анализ</h2>
          <CohortHeatmap cohorts={cohort} />
        </div>
      )}

        </>
      )}


    </div>
  );
}

const styles = {
  page: { maxWidth: 1400, margin: '0 auto', paddingBottom: 8 },
  pageHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 14,
    marginBottom: 20,
  },
  h1: {
    color: 'var(--text-primary)',
    fontSize: 26,
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.03em',
    fontFamily: 'var(--font-display)',
  },
  headActions: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 },
  infoBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'var(--surface-sheen), var(--bg-card)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '9px 14px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.01em',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: 'var(--shadow-sm)',
    transition: 'transform 0.2s ease, border-color 0.2s, box-shadow 0.2s, color 0.2s',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'color-mix(in srgb, var(--bg) 55%, rgba(0,0,0,.45))',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '20px 20px 40px',
    overflowY: 'auto',
  },
  modalPanel: {
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    maxWidth: 520,
    width: '100%',
    maxHeight: 'min(85vh, 640px)',
    overflow: 'auto',
    boxShadow: 'var(--shadow-lg)',
    marginTop: 4,
    animation: 'fadeIn 0.28s cubic-bezier(0.22, 1, 0.36, 1) both',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 18px',
    borderBottom: '1px solid var(--border)',
  },
  modalTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 650,
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.02em',
  },
  modalClose: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    color: 'var(--text-muted)',
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: { padding: '16px 18px 20px' },
  modalP: {
    margin: '0 0 14px 0',
    fontSize: 14,
    lineHeight: 1.55,
    color: 'var(--text-secondary)',
  },
  modalStrong: {
    margin: '0 0 10px 0',
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--accent)',
  },
  modalFooterNote: {
    margin: '16px 0 0 0',
    fontSize: 14,
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  h2: { color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, marginBottom: 12 },
  kpiRow: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 },
  tabs: {
    display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap',
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)', borderRadius: 14,
    padding: 5,
    boxShadow: 'var(--shadow-sm)',
  },
  tabBtn: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: 10,
    padding: '9px 18px',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'transform 0.18s ease, background 0.2s, color 0.2s, box-shadow 0.2s',
  },
  tabActive: {
    background: 'var(--accent-dim)', color: 'var(--accent)',
    fontWeight: 600, boxShadow: 'var(--shadow-sm)',
  },
  section: {
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 22,
    marginBottom: 18,
  },
  badge: {
    background: 'var(--accent-dim)', color: 'var(--accent)', border: `1px solid rgba(34,211,238,.25)`,
    padding: '8px 18px', borderRadius: 10, fontWeight: 600, fontSize: 14,
    transition: 'all .2s',
  },
  badgeActive: {
    background: 'rgba(34,211,238,.25)', borderColor: 'var(--accent)',
    boxShadow: '0 0 12px rgba(34,211,238,.15)',
  },
  hint: { color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 },
  tableSearch: {
    display: 'block',
    width: '100%',
    maxWidth: 420,
    marginBottom: 14,
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '9px 12px',
    fontSize: 13,
    outline: 'none',
    colorScheme: 'var(--color-scheme)',
    boxSizing: 'border-box',
  },
  chartBlock: {
    background: 'var(--surface-sheen), var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 18,
    marginBottom: 18,
    boxShadow: 'var(--shadow-sm)',
    transition: 'box-shadow 0.25s ease, border-color 0.2s',
  },
  groupPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  resetBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'var(--bg-card)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  closeXBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    background: 'var(--bg-card)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0,
    padding: 0,
    transition: 'color .15s, border-color .15s, background .15s',
  },
  groupCloseBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-muted)',
    fontSize: 24,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
    flexShrink: 0,
  },
  groupCellWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  groupBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: 'var(--accent)',
    background: 'rgba(34,211,238,.16)',
    border: '1px solid rgba(34,211,238,.35)',
  },
  kpiBlockedCard: {
    position: 'relative',
    borderRadius: 14,
    overflow: 'hidden',
  },
  kpiBlockedOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  kpiBlockedOverlayBlur: {
    position: 'absolute',
    inset: 0,
    backdropFilter: 'blur(6px)',
    background: 'rgba(9,14,28,.46)',
  },
  kpiBlockedOverlayLabel: {
    position: 'relative',
    zIndex: 3,
    color: 'var(--text-primary)',
    fontSize: 16,
    fontWeight: 700,
    textShadow: '0 4px 22px rgba(0,0,0,.5)',
    letterSpacing: 0.15,
  },
};
